import { FormikErrors, FormikTouched, useFormik } from 'formik';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
  addOrganization,
  getOrgs,
  updateOrganization,
} from '../../../../store/orgs/orgs.actions';
import { RootState } from '../../../../store/store';
import { Organization } from '../../../../types';

export type AddOrgForm = {
  orgName: string;
  url: string;
  email: string;
  location: string | null;
};

type UseOrgFormParams = {
  onClose: () => void;
  isAdd: boolean;
  org?: Organization | null;
  isMyOrgEdit?: boolean;
};

type UseOrgFormReturn = {
  handleCancel: () => void;
  errors: FormikErrors<AddOrgForm>;
  handleChange: (e: string | React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  touched: FormikTouched<AddOrgForm>;
  values: AddOrgForm;
};

export const useOrgForm = ({
  isAdd,
  onClose,
  isMyOrgEdit = false,
  org,
}: UseOrgFormParams): UseOrgFormReturn => {
  const dispatch = useDispatch();
  const { user, org: organization } = useSelector(
    (state: RootState) => state.auth
  );
  const message = useSelector((state: RootState) => state.orgs.successMsg);

  const validationSchema = Yup.object().shape({
    orgName: Yup.string().required('This field is required'),
    url: Yup.string().url(),
    email: Yup.string().email().required('This field is required'),
    location: Yup.string().nullable(),
  });

  const handleSubmit = (values: AddOrgForm) => {
    dispatch(
      isAdd
        ? addOrganization(values, user?.orgId || '', user?.userId || '')
        : updateOrganization(
            values,
            org?.orgId || '',
            org?.orgId === organization?.orgId
          )
    );
    onClose();
  };

  const formik = useFormik<AddOrgForm>({
    initialValues: {
      orgName: isAdd ? '' : org?.name || '',
      url: isAdd ? '' : org?.url || '',
      email: isAdd ? '' : org?.primaryEmail || '',
      location: isMyOrgEdit && org?.location ? org?.location : '',
    },
    onSubmit: handleSubmit,
    validationSchema,
    enableReinitialize: true,
  });

  const handleCancel = () => {
    formik.resetForm();
    onClose();
  };

  useEffect(() => {
    if (message) {
      dispatch(getOrgs());
    }
  }, [message, dispatch]);

  return {
    ...formik,
    handleCancel,
  };
};
